import {
  Target, Phone, CreditCard, BookOpen, AlertTriangle, Skull,
  X, Calendar, Users, Timer, Archive, ChevronRight, Zap
} from 'lucide-react';
import { formatDate } from '../../utils/locale';
import { formatMoneyMinor } from '../../utils/money';

const TYPE_ICON = { task: Target, call: Phone, payment: CreditCard, reading: BookOpen };
const PRIORITY_COLOR = {
  URGENT: 'var(--pd-crimson)', HIGH: 'var(--pd-warning)',
  MEDIUM: 'var(--pd-focus)', LOW: 'var(--pd-subtle)'
};

const ACTION_CONFIG = {
  do: { key: 'd', label: 'Do now', desc: 'Schedule into today', color: 'var(--pd-focus)', icon: Calendar },
  delegate: { key: 'g', label: 'Delegate', desc: 'Hand off to someone', color: 'var(--pd-warning)', icon: Users },
  defer: { key: 'f', label: 'Defer', desc: 'Push to later', color: 'var(--pd-muted)', icon: Timer },
  archive: { key: 'x', label: 'Archive', desc: 'Remove from queue', color: 'var(--pd-crimson)', icon: Archive },
};

export function SweepHeader({ processed, total, onClose }) {
  return (
    <div className="flex items-center justify-between px-8 py-4"
      style={{ borderBottom: '1px solid var(--pd-border)' }}>
      <div className="flex items-center gap-3">
        <Zap className="w-5 h-5" style={{ color: 'var(--pd-focus)' }} />
        <div>
          <div className="text-[15px] font-semibold" style={{ color: 'var(--pd-text)' }}>Sweep Mode</div>
          <div className="text-[11px]" style={{ color: 'var(--pd-subtle)' }}>
            Process your queue fast. One decision at a time.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="text-[12px] tabular-nums font-semibold" style={{ color: 'var(--pd-text)' }}>
          <span style={{ color: 'var(--pd-focus)' }}>{processed}</span> / {total}
        </div>
        <button data-testid="sweep-close" onClick={onClose}
          className="p-2 rounded-xl" style={{ color: 'var(--pd-muted)' }}>
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export function SweepProgressBar({ progress }) {
  return (
    <div className="h-1" style={{ background: 'var(--pd-panel-2)' }}>
      <div data-testid="sweep-progress" className="h-full transition-all duration-500"
        style={{ width: `${progress}%`, background: 'var(--pd-focus)' }} />
    </div>
  );
}

export function SweepItemCard({ item }) {
  const Icon = TYPE_ICON[item.type] || Target;
  const prColor = PRIORITY_COLOR[item.priority] || 'var(--pd-subtle)';
  const deferralCount = item.deferralCount || 0;

  return (
    <>
      {/* Type + priority strip */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: `${prColor}12`, color: prColor }}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-[10px] px-2 py-1 rounded-full font-bold uppercase tracking-wider"
          style={{ background: `${prColor}12`, color: prColor }}>
          {item.type}
        </span>
        <span className="text-[11px] font-semibold" style={{ color: prColor }}>{item.priority}</span>
        {item.businessShortCode && (
          <span className="text-[10px] px-2 py-1 rounded-full font-semibold ml-auto"
            style={{ background: `${item.businessColor}12`, color: item.businessColor }}>
            {item.businessShortCode}
          </span>
        )}
      </div>

      {/* Title */}
      <h2 data-testid="sweep-item-title" className="text-[28px] font-semibold leading-tight mb-3"
        style={{ color: 'var(--pd-text)', letterSpacing: '-0.04em' }}>
        {item.title}
      </h2>

      {/* Meta row */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <span className="text-[12px] tabular-nums flex items-center gap-1"
          style={{ color: 'var(--pd-muted)' }}>
          <Timer className="w-3.5 h-3.5" />{item.estimatedMinutes}m
        </span>
        {item.type === 'payment' && item.amountMinor != null && (
          <span className="text-[13px] tabular-nums font-semibold" style={{ color: 'var(--pd-text)' }}>
            {formatMoneyMinor(item.amountMinor, item.currency)}
          </span>
        )}
        {item.delegatedTo && (
          <span className="text-[11px] flex items-center gap-1 px-2 py-0.5 rounded-lg"
            style={{ background: 'rgba(180,83,9,0.08)', color: 'var(--pd-warning)' }}>
            <Users className="w-3 h-3" />{item.delegatedTo}
          </span>
        )}
        {deferralCount >= 2 && (
          <span className="text-[11px] flex items-center gap-1 px-2 py-0.5 rounded-lg font-semibold"
            style={{
              background: deferralCount >= 5 ? 'rgba(180,35,58,0.12)' : 'rgba(180,83,9,0.10)',
              color: deferralCount >= 5 ? 'var(--pd-crimson)' : 'var(--pd-warning)',
            }}>
            {deferralCount >= 5 ? <Skull className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
            Deferred {deferralCount}x
          </span>
        )}
        {item.scheduledDate && (
          <span className="text-[11px]" style={{ color: 'var(--pd-subtle)' }}>
            Scheduled: {formatDate(item.scheduledDate)}
          </span>
        )}
      </div>
    </>
  );
}

export function SweepActionGrid({ onAction }) {
  return (
    <div className="grid grid-cols-4 gap-3">
      {Object.entries(ACTION_CONFIG).map(([key, config]) => (
        <button key={key} data-testid={`sweep-action-${key}`}
          onClick={() => onAction(key)}
          className="flex flex-col items-center gap-2 py-5 rounded-2xl transition-all hover:scale-[1.03]"
          style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}>
          <config.icon className="w-6 h-6" style={{ color: config.color }} />
          <div>
            <div className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>{config.label}</div>
            <div className="text-[10px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>{config.desc}</div>
          </div>
          <kbd className="text-[11px] px-2 py-0.5 rounded-lg font-mono font-bold"
            style={{ background: 'var(--pd-panel-2)', color: config.color, border: '1px solid var(--pd-border)' }}>
            {config.key.toUpperCase()}
          </kbd>
        </button>
      ))}
    </div>
  );
}

export function SweepQueuePreview({ decisions, currentIdx }) {
  const total = decisions.length;
  return (
    <div className="mt-6 flex items-center gap-2 text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
      <ChevronRight className="w-3.5 h-3.5" />
      {currentIdx < total - 1 ? (
        <span>Next: <strong style={{ color: 'var(--pd-muted)' }}>{decisions[currentIdx + 1]?.title}</strong></span>
      ) : (
        <span>This is the last item in the queue</span>
      )}
    </div>
  );
}

export function SweepFooter({ actionLog }) {
  return (
    <div className="px-8 py-3 flex items-center justify-between"
      style={{ borderTop: '1px solid var(--pd-border)', background: 'var(--pd-panel)' }}>
      <div className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
        <kbd className="px-1.5 py-0.5 rounded font-mono text-[10px]" style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}>ESC</kbd> to exit
      </div>
      <div className="flex items-center gap-3 text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
        {actionLog.length > 0 && (
          <span>
            Last: {actionLog[actionLog.length - 1]?.action} "{actionLog[actionLog.length - 1]?.title?.slice(0, 25)}"
          </span>
        )}
      </div>
    </div>
  );
}

export function SweepExitOverlay({ processed }) {
  return (
    <div className="fixed inset-0 z-[301] flex items-center justify-center"
      style={{ background: 'var(--pd-overlay)' }}>
      <div className="rounded-3xl px-8 py-6 text-center" style={{ background: 'var(--pd-popover)', border: '1px solid var(--pd-popover-border)', boxShadow: 'var(--pd-shadow-3)' }}>
        <Zap className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--pd-focus)' }} />
        <div className="text-[18px] font-semibold" style={{ color: 'var(--pd-text)' }}>Queue swept</div>
        <div className="text-[12px] mt-1" style={{ color: 'var(--pd-muted)' }}>{processed} decisions processed</div>
      </div>
    </div>
  );
}
